/**
 * R63 (QA 28/10/2026, PR #1522) — o ÚNICO ponto que trata um 401 do motor.
 *
 * O DEFEITO. Qualquer 401 REST, até o de uma consulta secundária
 * (`GET /api/v1/funcionalidades`), chamava `logout()` do armazém: zerava
 * `userId` e o fornecedor de passe (`_getToken`) em silêncio. O Clerk continuava
 * logado e o `AuthGuard` não ressincronizava (suas dependências não mudam), então
 * a tela ficava viva por fora e morta por dentro: o toque seguinte virava envio
 * "obsoleto" ou `false` sem aviso, sem POST nenhum. Só o reload curava.
 *
 * A REGRA. 401 NÃO é logout automático. Primeiro tenta renovar o passe no Clerk
 * (sem cache), AVISA a pessoa ("reautenticando…" → "sessão renovada") e mantém a
 * identidade. Só desloga quando não dá para renovar OU quando o 401 volta sem
 * nenhuma resposta boa no meio (renovar não resolveu — não entra em laço).
 *
 * Componente nenhum chama `fetch`: quem avisa é este módulo, via toast (não é
 * tela nova — Princípio nº 1).
 */
import toast from 'react-hot-toast'
import i18n from 'i18next'
import { useAuthStore } from '@/shared/stores/auth'

const TOAST_ID = 'sessao-401'

export type UnauthorizedOutcome = 'renewed' | 'expired'

/** Renovou e ainda não houve resposta boa que prove que o passe novo vale. */
let renewedUnproven = false
let logoutFired = false
let inFlight: Promise<UnauthorizedOutcome> | null = null

/** Chamar em TODA resposta boa do motor: o passe novo provou que vale. */
export function markSessionHealthy(): void {
  renewedUnproven = false
  logoutFired = false
}

/** Nova sessão do Clerk: o próximo 401 definitivo pode deslogar de novo (não apaga a prova de renovação). */
export function rearmLogout(): void {
  logoutFired = false
}

/** Volta ao zero (nova autenticação de verdade e testes). */
export function resetAuthRecovery(): void {
  markSessionHealthy()
  inFlight = null
}

async function recover(): Promise<UnauthorizedOutcome> {
  const auth = useAuthStore.getState()
  if (!renewedUnproven && typeof auth.renewToken === 'function') {
    toast.loading(i18n.t('errors.sessionRenewing'), { id: TOAST_ID })
    const token = await auth.renewToken()
    if (token) {
      renewedUnproven = true
      toast.success(i18n.t('errors.sessionRenewed'), { id: TOAST_ID, duration: 4000 })
      return 'renewed'
    }
  }
  if (!logoutFired) {
    logoutFired = true
    auth.logout()
  }
  toast.error(i18n.t('errors.authError'), { id: TOAST_ID })
  return 'expired'
}

/** Várias chamadas em 401 ao mesmo tempo compartilham UMA renovação e UM aviso. */
export function handleUnauthorized(): Promise<UnauthorizedOutcome> {
  inFlight ??= recover().finally(() => {
    inFlight = null
  })
  return inFlight
}
